import { P2Alert, P2Button, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { Separator } from '../../../components/separator';
import { InfoCircle } from '../../../assets/svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { InfoCard } from '../../../components/info';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import hdbLogo from '../../../assets/images/hdb-logo.png';
import { useCallback, useRef } from 'react';
import AcknowledgementSegment from './acknowledgement-segment';
import { useAppSelector } from '../../../redux/hook';
import { useGetCpfWithdrawalOverviewQuery } from '../../../redux/services/getStageE';
import { CpfServiceStatusEnum } from '../../../models/enum/cpf-service-status.enum';
import { CpfFormStatus } from '../../../models/enum/cpf-form-status.enum';
import React from 'react';

function StageESubmitOverview() {
  let navigate = useNavigate();
  const location = useLocation();
  const { transaction_timestamp } = location.state || {};
  const queryParameters = new URLSearchParams(window.location.search);
  const application_id = queryParameters.get('application_id');
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const selectedCpfFormSN = useAppSelector((state) => state.app.ApplicationStageESlice.stage_e_selected_form_sn);

  React.useEffect(() => {
    // If no selected serial number, redirect
    if (!selectedCpfFormSN || selectedCpfFormSN.length === 0) {
      // navigate(`/`);
    }
  }, [selectedCpfFormSN, navigate]);

  const { data: prepareForCompletionDetails, isLoading: prepareForCompletionDetailsIsLoading } = useGetCpfWithdrawalOverviewQuery({
    application_id: application_id ?? '0',
    uinfin: loginUser?.uinfin ?? '',
    singpass_token: singpassToken ?? '0',
  });

  // Call API to check
  const coOwnerPendingCheck =
    prepareForCompletionDetails?.parties?.filter(
      (party) => party.cpfAccountNo !== loginUser?.uinfin && party.withdrawalRequests.find((request) => request.status === CpfFormStatus.Pending)
    ).length ?? 0;

  const pdfRef = useRef(null);
  const handleDownload = useCallback(() => {
    const input = pdfRef.current;
    if (!input) return;

    // Create image element for the HDB logo
    const logoImage = new Image();
    logoImage.src = hdbLogo;

    // Wait for the logo to load before proceeding
    logoImage.onload = () => {
      if (input) {
        const scale = 2; // Higher scale = higher RESOLUTION

        html2canvas(input, {
          backgroundColor: null,
          scale: scale, // Increase resolution
          useCORS: true, // Enable CORS for images
          logging: false, // Disable logging
          onclone: (clonedDoc) => {
            const container = clonedDoc.getElementById('polymer-stage-e-acknowledgement-container');
            if (container) {
              container.style.boxShadow = 'unset';
              container.style.border = 'unset';
            }
          },
        }).then((canvas) => {
          // Define constants for PDF dimensions and margins
          const LEFT_MARGIN = 40; // Left margin in pixels
          const RIGHT_MARGIN = 40; // Right margin in pixels
          const TOP_MARGIN = 20; // Top margin in pixels
          const PDF_WIDTH = 595; // A4 width in pixels (210mm at 72dpi)
          const PDF_HEIGHT = 842; // A4 height in pixels (297mm at 72dpi)

          // Create PDF with fixed A4 size in pixels
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [PDF_WIDTH, PDF_HEIGHT],
            compress: true, // Enable compression for better quality
          });

          // Calculate maximum available width considering margins
          const maxAvailableWidth = PDF_WIDTH - LEFT_MARGIN - RIGHT_MARGIN;
          const maxAvailableHeight = PDF_HEIGHT - TOP_MARGIN - 100; // Leave some space at bottom

          // Calculate natural content dimensions
          let contentWidth = (canvas.width / scale) * 0.95; // Initial size with slight reduction
          let contentHeight = (canvas.height / scale) * 0.95;

          // Check if content would exceed margins and scale if necessary
          const widthScaleFactor = maxAvailableWidth / contentWidth;
          const heightScaleFactor = maxAvailableHeight / contentHeight;
          const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

          contentWidth = contentWidth * scaleFactor;
          contentHeight = contentHeight * scaleFactor;

          // Add the HDB logo at the top with margin
          const logoWidth = PDF_WIDTH * 0.25; // 25% of page width
          const logoHeight = (49 * logoWidth) / 200; // Maintain aspect ratio
          const logoX = LEFT_MARGIN;
          const logoY = TOP_MARGIN;

          // Add logo with high quality
          pdf.addImage(hdbLogo, 'PNG', logoX, logoY, logoWidth, logoHeight, undefined, 'FAST');

          // Add a separator line below the logo
          const lineY = logoY + logoHeight + 10;
          const lineStartX = 20; // Slightly indented from left edge
          const lineEndX = PDF_WIDTH - 20; // Slightly indented from right edge
          pdf.setLineWidth(1); // Set line width to 1px
          pdf.setDrawColor(33, 37, 41); // Dark gray color (#212529)
          pdf.line(lineStartX, lineY, lineEndX, lineY);

          // Add the content below the separator line
          const contentX = (PDF_WIDTH - contentWidth) / 2; // Center horizontally
          const contentY = lineY + 15; // Position content below the separator line

          const imgData = canvas.toDataURL('image/png', 1.0); // Use maximum quality
          pdf.addImage(imgData, 'PNG', contentX, contentY, contentWidth, contentHeight, undefined, 'FAST');

          // Save with the original filename
          pdf.save('cpf-acknowledgement-receipt.pdf');
        });
      }
    };
  }, []);

  const handleNavigateBack = () => {
    navigate(`/application-coo/stage-e/prepare-for-completion?application_id=${application_id}`);
  };

  const handleNotifyCoApplicant = () => {
    navigate(`/application-coo/stage-e/notify-co-applicant?application_id=${application_id}`);
  };

  return (
    <ApplicationCooLayout title={'Apply to Withdraw CPF Funds Due to Change in Flat Ownership'}>
      <div className='mx-md-4'>
        <div className='polymer-application-coo__wrapper submit-overview-wrapper' ref={pdfRef}>
          <P2Alert variant={'success'} text={'Your application has been submitted.'}></P2Alert>

          <div className='submit-overview-text'>
            {/* <P2TextDescription
              color='#333'
              description_size={'1.125rem'}
              description_margin='0px'
              description={'Thank you for applying for the use of CPF savings and Home Protection Scheme (HPS) for the change in flat ownership.'}
            /> */}

            <P2TextDescription
              color='#333'
              description_size={'1.125rem'}
              description_margin='0px'
              description={
                'CPF Board will process your HPS application within five working days. A longer time may be required if they need to further assess your eligibility for an HPS cover. They may contact you if they require more information to assess your eligibility for an HPS cover. Please note that as HPS applications are processed and assessed individually, the processing time for applications submitted at the same time by an owner and his/her co-owner may differ'
              }
            />

            <P2TextDescription
              color='#333'
              description_size={'1.125rem'}
              description_margin='0px'
              description={
                'Upon approval of your HPS application, the CPF Board will deduct the HPS premium from your CPF Ordinary Account (OA) within two weeks from the legal ownership date. Thereafter, you will receive an HPS certificate within one to two weeks. If you have insufficient funds in your OA for the premium deduction, they will inform you on the options to pay the premium shortfall.'
              }
            />

            <P2TextDescription
              color='#333'
              description_size={'1.125rem'}
              description_margin='0px'
              description={
                'In the meantime, you may proceed to use your CPF savings for your monthly housing instalment while your HPS application is pending the CPF Board’s processing or if you are not eligible for a cover. '
              }
            />

            <p className='polymer-stage-e_18pt_description'>
              Please{' '}
              <a rel='noopener noreferrer' href={'https://www.cpf.gov.sg/service/contact-us'} target='_blank' className='polymer-text-links'>
                contact CPF Board
              </a>{' '}
              if you require further clarifications on HPS.
            </p>

            <P2TextDescription
              color='#333'
              description_size={'1.125rem'}
              description_margin='0px'
              description={
                'All applicants will need to sign the necessary documents and make payment for the necessary fees/ charges before the ownership change can be completed. Otherwise, the application may be cancelled, and fees will still apply. If your application to use your CPF savings to pay for your flat is unsuccessful, HDB will arrange with you on other modes of payment to proceed with the change in flat ownership.'
              }
            />
          </div>

          <div className='submit-overview-btn-grp'>
            <div className='submit-overview-btn'>
              <P2Button onClick={() => handleDownload()} variant={'outline-primary'} style={{ fontSize: '1.125rem' }} width='100%'>
                Save as PDF
              </P2Button>
            </div>

            {coOwnerPendingCheck >= 1 && (
              <div className='submit-overview-btn'>
                <P2Button onClick={() => handleNotifyCoApplicant()} variant={'outline-primary'} style={{ fontSize: '1.125rem' }} width='100%'>
                  Notify co-owner
                </P2Button>
              </div>
            )}

            <div className='submit-overview-btn'>
              <P2Button onClick={() => handleNavigateBack()} variant={'primary-1'} style={{ fontSize: '1.125rem' }} width='100%'>
                Back to Stage E
              </P2Button>
            </div>
          </div>

          <Separator variant='dashed' style={{ margin: '24px 0px' }} />
          <div id='polymer-stage-e-acknowledgement-container'>
            <AcknowledgementSegment transactionTimestamp={transaction_timestamp} />
          </div>
        </div>
      </div>
    </ApplicationCooLayout>
  );
}

export default StageESubmitOverview;
